import {
  Autocomplete,
  Box,
  CircularProgress,
  Stack,
  styled,
  TextField,
  Button as MuiButton,
  Pagination,
} from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { fetchProductCategoryAsync } from "src/infrastructure/http/appApiService";
import { useCountry } from "src/components/Context/CountryContext";
import { EmptyListState } from "src/components/EmptyState/EmptyListState";
import { SearchOutlined } from "@mui/icons-material";
import DeleteIconOutlined from "@mui/icons-material/DeleteOutlined";
import { ValidationLabel } from "src/components/Form";
import { ProductCategoryResponse } from "src/models/product/ProductCategoryResponse";
import { ProductCategoryRow } from "./ProductCategoryRow";

const AutoCompleteTextField = styled(TextField)`
  .MuiInputBase-root {
    border-radius: 0;
    padding-top: 6px;
    padding-bottom: 6px;
  }
`;

const CustomRemoveButton = styled(MuiButton)(({ theme }) => ({
  backgroundColor: "transparent !important",
  color: theme.palette.error.main,
  textTransform: "none",
  padding: "6px 16px",
}));

type ProductCategorySelectProps = {
  name: string;
  value: number[];
  onChange: (value: number[]) => void;
  hasError?: boolean;
  errorMessage?: string;
};

export const ProductCategorySelect: React.FC<ProductCategorySelectProps> = ({
  name,
  value,
  onChange,
  hasError,
  errorMessage,
}: ProductCategorySelectProps) => {
  const { country } = useCountry();

  const [productCategoryOptions, setProductCategoryOptions] = useState<ProductCategoryResponse[]>([]);
  const [filteredProductCategory, setFilteredProductCategory] = useState<ProductCategoryResponse[]>([]);
  const [productCategorySearchValue, setProductCategorySearchValue] = useState("");
  const [showOptions, setShowOptions] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [validationMessage, setValidationMessage] = useState<string>(errorMessage);
  const [selectedProductCategory, setSelectedProductCategory] = useState<ProductCategoryResponse[]>([]);

  const [page, setPage] = useState(1);
  const pageSize =  10;
  const totalCount = selectedProductCategory.length;

  const paginatedProductCategories = useMemo(() => {
    const start = (page - 1) * pageSize;
    return selectedProductCategory.slice(start, start + pageSize);
  }, [selectedProductCategory, page]);

  const handlePageChange = useCallback((event: React.ChangeEvent<unknown>, newPage: number) => {
    setPage(newPage);
  }, []);

  useEffect(() => {
    const maxPage = Math.max(1, Math.ceil(totalCount / pageSize));
    if (page > maxPage) {
      setPage(maxPage);
    }
  }, [totalCount, page]);

  useEffect(() => {
    setIsLoading(true);
    fetchProductCategoryAsync(country)
      .then((results) => {
        setProductCategoryOptions(results ?? []);
        setIsLoading(false);
      })
      .catch(() => {
        setProductCategoryOptions([]);
        setIsLoading(false);
        setValidationMessage("Failed to load Product Categories. Please try again later.");
      });
  }, [country]);

  useEffect(() => {
    if (!productCategoryOptions) {
      setFilteredProductCategory([]);
      return;
    }
    const search = productCategorySearchValue.toLowerCase();
    setFilteredProductCategory(
      productCategoryOptions.filter(
        (pg) => pg.name?.toLowerCase().includes(search) || pg.id?.includes(search)
      )
    );
  }, [productCategorySearchValue, productCategoryOptions]);

  useEffect(() => {
    if (!value || productCategoryOptions.length === 0) {
      setSelectedProductCategory([]);
      return;
    }
    const processedValue = value.map(String);
    const selected = productCategoryOptions.filter((pg) => processedValue.includes(pg.id));
    setSelectedProductCategory(selected);
  }, [value, productCategoryOptions]);

  const handleProductCategoryAdded = useCallback(
    (newValue: ProductCategoryResponse) => {
      const newProductCategoryIds = [...value, parseInt(newValue.id)];
      onChange(newProductCategoryIds);
      setProductCategorySearchValue("");
    },
    [value, onChange]
  );

  const handleProductCategoryRemoved = useCallback(
    (productCategory: ProductCategoryResponse) => {
      const newProductCategoryIds = value.filter((category) => category !== parseInt(productCategory.id));
      onChange(newProductCategoryIds);
    },
    [value, onChange]
  );

  const handleClearAllItems = useCallback(() => {
    onChange([]);
  }, [onChange]);

  return (
    <>
      <Stack direction="column" spacing={2}>
        <Stack direction="row" spacing={2} marginBottom={2} marginTop={0} justifyContent="center">
          <Autocomplete
            id={`productcategory-autocomplete-${name}`}
            open={showOptions}
            getOptionLabel={(option) => (typeof option === "string" ? option : option.name)}
            filterOptions={(x) => x}
            options={productCategorySearchValue == "" ? productCategoryOptions : filteredProductCategory}
            autoComplete
            inputValue={productCategorySearchValue}
            filterSelectedOptions
            loading={isLoading}
            loadingText="Searching for Product Categories..."
            noOptionsText="No Product Categories found"
            style={{ boxShadow: "2px 2px 0 0 var(--palette-primary-shadow__1yqyf5p6)", flexGrow: 1 }}
            onInputChange={(_, newInputValue) => {
              setProductCategorySearchValue(newInputValue);
              setShowOptions(true);
            }}
            onFocus={() => setShowOptions(true)}
            onBlur={() => setShowOptions(false)}
            renderInput={(params) => <AutoCompleteTextField {...params} placeholder="Search by Product Category" fullWidth />}
            renderOption={(_, option) => {
              const isAdded = value.includes(parseInt(option.id));
              return (
                <ProductCategoryRow
                  key={option.id}
                  productCategory={option}
                  isAdded={isAdded}
                  onClick={isAdded ? handleProductCategoryRemoved : handleProductCategoryAdded}
                />
              );
            }}
            slotProps={{
              popper: {
                disablePortal: true,
                placement: "bottom",
              },
            }}
          />
        </Stack>

        {value.length > 0 ? (
          <Box padding={3} border={1} borderColor={(theme) => theme.palette.grey[400]}>
            {isLoading && selectedProductCategory.length === 0 ? (
              <Box display="flex" justifyContent="center" alignItems="center" height="100px">
                <CircularProgress />
              </Box>
            ) : (
              paginatedProductCategories.map((productCategory) => (
                <ProductCategoryRow
                  key={productCategory.id}
                  productCategory={productCategory}
                  isAdded={true}
                  onClick={handleProductCategoryRemoved}
                />
              ))
            )}
          </Box>
        ) : (
          <EmptyListState
            icon={SearchOutlined}
            title="No Product Categories added"
            message="Please add Product Categories by searching from the field above"
          />
        )}

        {selectedProductCategory.length > 0 && (
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <CustomRemoveButton
              startIcon={<DeleteIconOutlined style={{ color: "#d32f2f" }} />}
              onClick={handleClearAllItems}
              disabled={isLoading || selectedProductCategory.length === 0}
            >
              Remove all Product Categories
            </CustomRemoveButton>

            {totalCount > pageSize && (
              <Pagination
                count={Math.ceil(totalCount / pageSize)}
                page={page}
                onChange={handlePageChange}
                disabled={isLoading}
              />
            )}
            <div style={{ width: "175px" }}></div>
          </Stack>
        )}
      </Stack>
      <ValidationLabel errorMessage={validationMessage} />
    </>
  );
};
